import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page } from "./_shared";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Key, Copy, RotateCw, Webhook, Boxes, ShoppingBag, ShieldCheck, Eye, EyeOff } from "lucide-react";
import ImportCenter from "@/components/ImportCenter";

const PLATFORMS = [
  {
    id: "bling",
    name: "Bling ERP",
    desc: "ERP brasileiro líder em PMEs. Sincronize vendas e estoque automaticamente.",
    color: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    steps: [
      "Acesse o painel Bling → Integrações → Webhooks",
      "Crie um novo webhook do tipo 'Pedido aprovado'",
      "URL de destino: cole a Webhook URL acima",
      "No campo 'Headers', adicione: X-API-Key = sua chave SmartStockAI",
      "Salve. A cada pedido aprovado, o estoque na SmartStockAI será atualizado.",
    ],
  },
  {
    id: "shopify",
    name: "Shopify",
    desc: "Plataforma de e-commerce global. Webhooks nativos.",
    color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    steps: [
      "Shopify Admin → Settings → Notifications → Webhooks",
      "Create webhook: Event = 'Order paid', Format = JSON",
      "URL: cole a Webhook URL acima",
      "Você precisará de um middleware (Make/Zapier) para mapear o JSON do Shopify para o formato SmartStockAI {sku, qty}.",
    ],
  },
  {
    id: "ml",
    name: "Mercado Livre",
    desc: "Marketplace brasileiro #1. Sincronização via webhooks de vendas.",
    color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    steps: [
      "ML Developers → My applications → Notifications",
      "Topic: 'orders_v2', Callback URL: a Webhook URL acima",
      "Use um intermediário (n8n/Make) para chamar /api/integrations/sale com o SKU e qty do pedido.",
    ],
  },
  {
    id: "vtex",
    name: "VTEX",
    desc: "Plataforma enterprise de e-commerce.",
    color: "bg-pink-500/15 text-pink-400 border-pink-500/30",
    steps: [
      "VTEX Admin → Account Settings → Hooks",
      "Configure o hook 'orderInvoiced' → URL: a Webhook URL acima",
      "Use VTEX IO ou middleware para mapear orderItems[].refId → SKU.",
    ],
  },
  {
    id: "woo",
    name: "WooCommerce",
    desc: "WordPress e-commerce. Webhooks nativos via REST.",
    color: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    steps: [
      "WooCommerce → Settings → Advanced → Webhooks → Add webhook",
      "Topic: 'Order updated', Status: 'Active', URL: a Webhook URL acima",
      "Use uma action 'woocommerce_order_status_completed' (PHP) para enviar payload simplificado {sku, qty}.",
    ],
  },
];

export default function Integrations() {
  const [keyData, setKeyData] = useState(null);
  const [reveal, setReveal] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { api.get("/integrations/key").then((r) => setKeyData(r.data)); }, []);

  const regen = async () => {
    if (!window.confirm("Gerar nova chave invalidará a anterior. Sistemas integrados precisarão ser atualizados. Continuar?")) return;
    try {
      const r = await api.post("/integrations/key/regenerate");
      setKeyData({ ...keyData, api_key: r.data.api_key });
      toast.success("Nova chave gerada");
    } catch {
      toast.error("Erro");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  if (!keyData) return <Skeleton className="h-96" />;

  const masked = keyData.api_key ? keyData.api_key.slice(0, 8) + "•".repeat(20) + keyData.api_key.slice(-4) : "";
  const webhookSale = `${backendUrl}/api/integrations/sale`;
  const webhookRestock = `${backendUrl}/api/integrations/restock`;

  const curlExample = `curl -X POST ${webhookSale} \\
  -H "X-API-Key: ${keyData.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{"sku":"SKU-EXEMPLO","qty":1,"external_id":"pedido_123"}'`;

  const nodeExample = `import axios from "axios";

await axios.post("${webhookSale}", {
  sku: "SKU-EXEMPLO",
  qty: 1,
  external_id: "pedido_123"
}, {
  headers: { "X-API-Key": "${keyData.api_key}" }
});`;

  const pythonExample = `import requests

requests.post(
    "${webhookSale}",
    json={"sku": "SKU-EXEMPLO", "qty": 1, "external_id": "pedido_123"},
    headers={"X-API-Key": "${keyData.api_key}"},
)`;

  return (
    <Page
      title="Integrações"
      subtitle="Conecte seu PDV, e-commerce ou ERP. Cada venda atualiza seu estoque automaticamente."
    >
      {/* Centro de importação CSV — primeira coisa que o cliente vê */}
      <ImportCenter />

      {/* API Key card */}
      <div className="p-6 rounded-xl border border-primary/30 bg-card glow-coral">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-display font-semibold">Sua chave de API</h3>
              <p className="text-xs text-muted-foreground">Use no header <code className="text-primary">X-API-Key</code> em todas as chamadas</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="reveal-key-btn" variant="outline" size="sm" onClick={() => setReveal(!reveal)}>
              {reveal ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {reveal ? "Ocultar" : "Mostrar"}
            </Button>
            <Button data-testid="copy-key-btn" variant="outline" size="sm" onClick={() => copy(keyData.api_key)}>
              <Copy className="w-4 h-4 mr-2" /> Copiar
            </Button>
            <Button data-testid="regen-key-btn" variant="outline" size="sm" onClick={regen} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
              <RotateCw className="w-4 h-4 mr-2" /> Gerar nova
            </Button>
          </div>
        </div>
        <code data-testid="api-key-display" className="block w-full p-4 rounded-lg bg-secondary/40 border border-border font-mono text-sm break-all">
          {reveal ? keyData.api_key : masked}
        </code>
      </div>

      {/* Endpoints */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
        <EndpointCard icon={ShoppingBag} title="Registrar venda" url={webhookSale} method="POST" desc="Decrementa estoque automaticamente. Suporta external_id para idempotência." />
        <EndpointCard icon={Boxes} title="Reabastecer estoque" url={webhookRestock} method="POST" desc="Adiciona unidades ao estoque. Útil ao receber compras." />
      </div>

      {/* Code examples */}
      <div className="p-6 rounded-xl border border-border bg-card mt-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Webhook className="w-4 h-4 text-primary" /> Exemplos de código</h3>
        <Tabs defaultValue="curl">
          <TabsList>
            <TabsTrigger value="curl" data-testid="tab-curl">cURL</TabsTrigger>
            <TabsTrigger value="node" data-testid="tab-node">Node.js</TabsTrigger>
            <TabsTrigger value="python" data-testid="tab-python">Python</TabsTrigger>
          </TabsList>
          <TabsContent value="curl"><CodeBlock code={curlExample} /></TabsContent>
          <TabsContent value="node"><CodeBlock code={nodeExample} /></TabsContent>
          <TabsContent value="python"><CodeBlock code={pythonExample} /></TabsContent>
        </Tabs>
      </div>

      {/* Platform connectors */}
      <div className="mt-8">
        <h2 className="font-display text-xl font-semibold mb-2">Guias de conexão</h2>
        <p className="text-sm text-muted-foreground mb-5">Passo-a-passo para conectar com as principais plataformas brasileiras e globais.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PLATFORMS.map((p) => (
            <div key={p.id} data-testid={`platform-${p.id}`} className="p-6 rounded-xl border border-border bg-card hover:border-primary/40 transition">
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs px-2.5 py-1 rounded-md border font-mono ${p.color}`}>{p.name}</span>
                {p.id === "bling" && <span className="text-[10px] px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30 uppercase tracking-wider">recomendado</span>}
              </div>
              <p className="text-sm text-muted-foreground mb-4">{p.desc}</p>
              <ol className="space-y-2 text-sm">
                {p.steps.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono text-xs text-primary mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-foreground/80">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 p-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 flex items-start gap-4">
        <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-emerald-400">Boas práticas de segurança</div>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
            <li>Nunca exponha sua chave API em código frontend ou repositórios públicos.</li>
            <li>Sempre envie <code className="text-emerald-400">external_id</code> nas vendas para evitar duplicações.</li>
            <li>Em caso de vazamento, gere uma nova chave imediatamente — a anterior é invalidada.</li>
          </ul>
        </div>
      </div>
    </Page>
  );
}

const EndpointCard = ({ icon: Icon, title, url, method, desc }) => (
  <div className="p-5 rounded-xl border border-border bg-card">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-primary" />
      <h3 className="font-display font-semibold">{title}</h3>
    </div>
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs font-mono px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{method}</span>
      <code className="text-xs font-mono text-muted-foreground truncate">{url}</code>
    </div>
    <p className="text-sm text-muted-foreground">{desc}</p>
  </div>
);

const CodeBlock = ({ code }) => {
  const copy = () => { navigator.clipboard.writeText(code); toast.success("Copiado"); };
  return (
    <div className="relative">
      <Button variant="outline" size="sm" onClick={copy} className="absolute top-2 right-2"><Copy className="w-3 h-3 mr-1" />Copiar</Button>
      <pre className="p-4 rounded-lg bg-secondary/40 border border-border font-mono text-xs overflow-x-auto whitespace-pre-wrap">{code}</pre>
    </div>
  );
};
